/**
 * Shower wall detection and region computation helpers.
 *
 * Finds showers on a wall (via parentWallIds) and computes a tiling region
 * sized to cover the shower's footprint on that wall using shared-lib geometry.
 */

import { belongsToWall } from './parentWallHelpers';
import { isObjectType } from './objectTypeUtils';
import {
  buildShowerFootprint,
  calculateShowerWallTiling,
  calculateShowerWallRegion,
  showerTileRegionOptions,
} from '@shared-lib/geometry/showerWallTiling';

/**
 * Find a shower object on a given wall.
 *
 * First checks parentWallIds (direct assignment). If no match, uses geometric
 * alignment of the shower footprint to detect walls the shower touches
 * (e.g. side walls that aren't in parentWallIds).
 *
 * @param {string} wallId - Wall ID to check
 * @param {Object} room   - Room data with .objects and .walls arrays
 * @returns {Object|null}  The shower object, or null if none found
 */
export function findShowerOnWall(wallId, room) {
  if (!wallId || !room?.objects) return null;

  // Pass 1: check parentWallIds (fast path)
  for (const obj of room.objects) {
    if (isObjectType(obj, 'shower') && belongsToWall(obj, wallId)) {
      return obj;
    }
  }

  // Pass 2: geometric check — does any shower's footprint
  // have an edge aligned with this wall?
  for (const obj of room.objects) {
    if (!isObjectType(obj, 'shower')) continue;

    const footprint = buildShowerFootprint(obj);
    const tilingResult = calculateShowerWallTiling(footprint, room);

    if (tilingResult.sides.some((side) => side.wallId === wallId)) {
      return obj;
    }
  }

  return null;
}

/**
 * Find ALL wall IDs that a shower touches (for multi-wall tiling).
 *
 * Uses the shared-lib geometry to build the shower footprint and detect
 * which walls each edge aligns with.
 *
 * @param {Object} shower - Shower object with center, dimensions, rotation
 * @param {Object} room   - Room data with .walls array
 * @returns {string[]}     Array of wallIds the shower touches
 */
export function findAllShowerWallIds(shower, room) {
  if (!shower || !room?.walls) return [];

  const footprint = buildShowerFootprint(shower);
  const tilingResult = calculateShowerWallTiling(footprint, room);

  return tilingResult.sides
    .filter((side) => side.wallId)
    .map((side) => side.wallId);
}

/**
 * Compute the shower wall tiling region (percentage coordinates) for a
 * shower's footprint projected onto a specific wall.
 *
 * Uses shared-lib geometry functions (buildShowerFootprint,
 * calculateShowerWallTiling, calculateShowerWallRegion) which handle:
 * - Building the 4-corner shower footprint from center/dimensions/rotation
 * - Detecting which shower edge aligns with which wall
 * - Projecting the shower edge onto wall coordinates (with bottomCorners UV flip)
 *
 * Cut to the shower tile rule — the same one the cloud tiler uses
 * (`showerTileRegionOptions`): the footprint + 2" past each open end, run on
 * to a wall end within 4", 84" high (Standard) or to the ceiling (Full).
 *
 * @param {Object} shower - Shower object with center, dimensions, rotation
 * @param {string} wallId - Wall ID to compute the region for
 * @param {Object} room   - Room data with .walls array
 * @param {'standard'|'full'} [tileHeight] - the height the tile runs to;
 *   omitted, Standard
 * @returns {Object}       { x, y, width, height } in percentages 0-100
 */
export function computeShowerWallRegion(shower, wallId, room, tileHeight) {
  if (!shower || !wallId || !room?.walls) {
    return { x: 0, y: 0, width: 100, height: 100 };
  }

  // Step 1: Build the shower's XZ footprint (4 corners)
  const footprint = buildShowerFootprint(shower);

  // Step 2: Find which shower edges align with which walls
  const tilingResult = calculateShowerWallTiling(footprint, room);

  // Step 3: Find the tiling side that matches our target wall
  const tilingSide = tilingResult.sides.find((side) => side.wallId === wallId);
  if (!tilingSide) {
    return { x: 0, y: 0, width: 100, height: 100 };
  }

  // Step 4: Find the wall data from room.walls
  const wall = room.walls.find((w) => w.id === wallId);
  if (!wall) {
    return { x: 0, y: 0, width: 100, height: 100 };
  }

  // Step 5: Project the shower edge onto wall coordinates → percentage region
  const region = calculateShowerWallRegion(
    shower,
    wall,
    tilingSide,
    showerTileRegionOptions(tileHeight, shower)
  );
  if (!region) {
    return { x: 0, y: 0, width: 100, height: 100 };
  }

  return region;
}
